import * as React from 'react';
import { cloneDeep, filter, find, forEach, isArray, isFunction, map, uniqBy } from 'lodash';

import { IProspect } from 'src/common/models/member';
import { IMemberProgram } from 'src/common/models/program';

import { useCommunities } from './useCommunities';
import { useInviteContext } from './useInviteContext';
import { useInviteMember } from './useInviteMember';
import { usePrograms } from './usePrograms';
import { useAllPrograms } from './useAllPrograms';
import { useResources } from './useResources';
import { useUpdateMemberPrograms } from './useUpdateMemberPrograms';
import { IInviteProps } from '../Invite';

const { useCallback, useEffect } = React;

type TProps = Pick<IInviteProps, 'onInvite' | 'preloadProspects' | 'prospect'>;

export const useInvite = (props: TProps) => {
  const {
    onInvite,
    preloadProspects,
    prospect,
  } = props;

  const { updateMemberPrograms } = useInviteContext();
  const {
    loading: isFetchingResources,
    resourceId,
  } = useResources();
  const {
    isFetching: isFetchingCommunities,
    communities,
  } = useCommunities();
  const {
    isFetching: isFetchingPrograms,
    programs,
  } = usePrograms();
  const {
    isFetching: isFetchingProspectPrograms,
  } = useUpdateMemberPrograms(preloadProspects);
  const {
    sendInvite,
    ...inviteStatus
  } = useInviteMember({ prospect });
  const {
    allPrograms,
    isFetching: isFetchingAllPrograms,
  } = useAllPrograms();

  /**
   * Update programs on invite
   */
  const programIds = map(allPrograms, (p) => p.id).sort().join(',');
  const updateProgramStatusForProspects = useCallback(
    (
      invitedProspect: IInviteProps['prospect'],
      programId: IMemberProgram['id'],
    ) => {
      const prospects: IProspect[] = isArray(invitedProspect) ? invitedProspect : [invitedProspect];
      const updatedPrograms: IMemberProgram[] = map(prospects, (prospect) => {
        const program = find(allPrograms, (program) => program.id === programId) as IMemberProgram;
        return {
          ...cloneDeep(program),
          username: prospect.socialAccount.username,
          social_account_id: prospect.socialAccount.id,
          status: program?.status || 'invited',
        };
      });
      if (updatedPrograms.length > 0) {
        if (isFunction(onInvite)) {
          onInvite(invitedProspect, uniqBy(updatedPrograms, (p) => p.title));
        }
        forEach(
          prospects,
          ({ socialAccount }) => updateMemberPrograms(
            socialAccount.id,
            filter(
              updatedPrograms,
              (program) => (
                program.username
                  ? program.username === socialAccount.username
                  : program.social_account_id === socialAccount.id
              ),
            ),
          ),
        );
      }
    },
    [programIds],
  );

  useEffect(() => {
    if (inviteStatus && inviteStatus.isInvited) {
      updateProgramStatusForProspects(inviteStatus.prospect, inviteStatus.programId); // Update the status to 'invited'
    }
  }, [inviteStatus.isInvited]);

  return {
    communities,
    programs,
    resourceId,
    inviteStatus,
    sendInvite,
    allPrograms,

    isFetching: (
      isFetchingCommunities ||
      isFetchingPrograms ||
      isFetchingResources ||
      isFetchingProspectPrograms ||
      isFetchingAllPrograms
    ),
    error: (
      inviteStatus.error
    ),
  };
};
